import { UUID } from "crypto";
import { FileTypes, Study, StudyFile } from "../models";
import axios from "axios";

import useUserContext from "../../Authorization/useUserContext";
import { buildHeaders } from "../../../api/utils";
import {
  SaveStudy,
  StudyType,
  StudyStatus,
  FileType,
  AudioAssignment,
  FileEditState,
  VerificationResponse,
} from "./manageStudyModels";

const getFileByType = (files: StudyFile[], fileType: FileTypes) => {
  return files.find((file) => file.file_type == fileType);
};

export default function useStudyService() {
  const API_URI = import.meta.env.VITE_APP_API_URI;
  const user = useUserContext();

  const editModelFromMetadata = (metadata_study: Study) => {
    const study: SaveStudy = {
      study_name: metadata_study.study_name,
      study_type: metadata_study.study_type as StudyType,
      study_status: metadata_study.study_status as StudyStatus,
      start_date: metadata_study.study_start_date,
      end_date: metadata_study.study_end_date,

      vendor: metadata_study.vendor.vendor_name,
      product: metadata_study.product.product_name,
      therapeutic_area: metadata_study.therapeutic_area.therapeutic_area_name,

      owner: metadata_study.owner.auth_user_id,

      editors: metadata_study.editors.map((_) => _.user.auth_user_id),

      // These will be undefined if this is a "POWERPOINT" study
      screener_summary_guid: getFileByType(metadata_study.files, FileType.SCREENER_SUMMARY)?.uploaded_file
        ?.uploaded_file_guid,
      screener_guide_guid: getFileByType(metadata_study.files, FileType.SCREENER_GUIDE)?.uploaded_file
        ?.uploaded_file_guid,
      discussion_guide_guid: getFileByType(metadata_study.files, FileType.DISCUSSION_GUIDE)?.uploaded_file
        ?.uploaded_file_guid,

      // API has two models for save AddPresentationStudyRequest and AddInterviewStudyRequest
      // We are seeing if we can get away with having a singular save model and only setting
      // properties based on the inferred study_format
      audio_assignments: metadata_study.interviews.length
        ? metadata_study.interviews.map(
            (interview) =>
              ({
                audio_file_id: interview.audio_file?.file_metadata?.audio_file_id as string | undefined,
                deepsense_file_guid: interview.audio_file?.uploaded_file?.uploaded_file_guid,
                respondent_ids: interview.respondents.map((r) => r.respondent_id),
              }) as AudioAssignment,
          )
        : undefined, // Note: audio_assignments should be "undefined" if study_format == "POWERPOINT"

      // This will be undefined if "TRANSCRIPT" study
      // EditPresentationStudyRequest
      power_point_guid: getFileByType(metadata_study.files, FileType.POWERPOINT)?.file_guid,
    };

    return study;
  };

  /** Attempts to extract audio_file_id from StudyFile, will return undefined for any non AUDIO StudyFile */
  const getAudioFileId = (file: StudyFile) => {
    const audio_file_id = file?.file_metadata?.audio_file_id as string | undefined;
    return audio_file_id;
  };

  const editFilesFromMetadata = (metadata_study: Study): FileEditState[] => {
    const files = metadata_study.files.map((_file) => {
      // Pull the audio_file_id from the study.files instance
      const audio_file_id = getAudioFileId(_file);

      // Attempt to locate an interview (if file is AUDIO)
      const interview = metadata_study.interviews.find(
        (_) => _?.audio_file?.file_metadata?.audio_file_id == audio_file_id,
      );

      const _fileEditState = {
        file_type: FileType[_file.file_type],

        uploaded_file_guid: _file.uploaded_file?.uploaded_file_guid,
        s3_file_location: _file.uploaded_file?.s3_file_location,
        original_name: _file.uploaded_file?.original_name,

        // This will not be set when not AUDIO file
        audio_file_id,
        process_status: interview?.processes.at(0)?.process_status,
      } as FileEditState;

      return _fileEditState;
    });

    // Splice in any missing file types so that the app works as expected
    const studyGuideTypes = [
      FileType.SCREENER_SUMMARY,
      FileType.SCREENER_GUIDE,
      FileType.DISCUSSION_GUIDE,
      FileType.POWERPOINT,
    ];

    studyGuideTypes.forEach((file_type) => {
      if (!files.find((_) => _.file_type == file_type)) {
        files.push({ file_type });
      }
    });

    return files;
  };

  type SaveOptions = {
    transcribe?: boolean;
  };

  const saveStudy = async (study: SaveStudy, study_guid: UUID, options: SaveOptions) => {
    const save_payload = { ...study, ...options };
    const response = await axios.put<Study>(`${API_URI}/pmr/v2/study/${study_guid}`, save_payload, {
      headers: buildHeaders(user),
    });

    return response.data;
  };

  const verifyScreenerSummary = async (deepsense_file_guid: UUID) => {
    const response = await axios.get<VerificationResponse>(
      `${API_URI}/pmr/v2/verify_screener_summary/${deepsense_file_guid}`,
      {
        headers: buildHeaders(user),
      },
    );

    if (axios.isAxiosError(response)) {
      throw response;
    }

    return response.data;
  };

  return {
    editModelFromMetadata,
    editFilesFromMetadata,
    saveStudy,

    /** Triggers a verification process on an uploaded Screener Summary, and returns a a  */
    verifyScreenerSummary,
  };
}
